import { useState, type FormEvent } from 'react';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader } from '@/components/shared/page-header';
import { useCostViewerMutations, useCostViewers } from '@/hooks/use-analytics-v2';
import { CostPricesSection } from '@/modules/analytics-v2/components/cost-prices-section';

const NO_ACCESS = "You don't have access to manage AI cost viewers.";

/** 403 gets a fixed sentence; anything else shows the backend's message (the api client puts it on `message`). */
const describeError = (err: unknown): string => {
  if (isAxiosError(err) && err.response?.status === 403) return NO_ACCESS;
  return err instanceof Error && err.message ? err.message : 'Something went wrong.';
};

const formatAddedAt = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * Hidden page (not in the menu) for the AI cost allowlist: who can see the AI Cost tab and the
 * cost API, and the AI price list behind every cost. The backend enforces both; this page only
 * lists and edits them.
 */
export default function AiCostAccessPage() {
  const query = useCostViewers();
  const { add, remove } = useCostViewerMutations();
  const [email, setEmail] = useState('');
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const run = async (action: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setActionError(null);
    try {
      await action();
      return true;
    } catch (err) {
      setActionError(describeError(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onAdd = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    if (await run(() => add(trimmed))) setEmail('');
  };

  const onConfirmRemove = async (target: string) => {
    if (await run(() => remove(target))) setPendingRemoval(null);
  };

  const viewers = query.data?.viewers ?? [];

  return (
    <div className='flex flex-col gap-6'>
      <PageHeader
        title='AI cost settings'
        description='Emails on this list can see AI cost data in analytics. Everyone else gets no AI Cost tab.'
      />

      {query.isError ? (
        <p role='alert' className='text-sm text-destructive'>
          {describeError(query.error)}
        </p>
      ) : (
        <>
          <form onSubmit={onAdd} className='flex max-w-md items-end gap-2'>
            <div className='flex flex-1 flex-col gap-1.5'>
              <Label htmlFor='cost-viewer-email'>Email</Label>
              <Input
                id='cost-viewer-email'
                type='email'
                autoComplete='off'
                placeholder='name@kim.cc'
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <Button type='submit' disabled={busy || !email.trim()}>
              Add
            </Button>
          </form>

          {actionError && (
            <p role='alert' className='text-sm text-destructive'>
              {actionError}
            </p>
          )}

          {query.isLoading ? (
            <p className='text-sm text-muted-foreground'>Loading…</p>
          ) : (
            <Table aria-label='AI cost viewers'>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Added by</TableHead>
                  <TableHead>Added at</TableHead>
                  <TableHead className='text-right'>
                    <span className='sr-only'>Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {viewers.map((viewer) => (
                  <TableRow key={viewer.email}>
                    <TableCell>{viewer.email}</TableCell>
                    <TableCell>{viewer.addedBy ?? '—'}</TableCell>
                    <TableCell>{formatAddedAt(viewer.createdAt)}</TableCell>
                    <TableCell className='text-right'>
                      {pendingRemoval === viewer.email ? (
                        <div className='flex items-center justify-end gap-2'>
                          <span className='text-sm'>Remove {viewer.email}?</span>
                          <Button
                            variant='destructive'
                            size='sm'
                            disabled={busy}
                            onClick={() => onConfirmRemove(viewer.email)}
                          >
                            Confirm remove
                          </Button>
                          <Button variant='outline' size='sm' disabled={busy} onClick={() => setPendingRemoval(null)}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant='outline'
                          size='sm'
                          disabled={busy}
                          aria-label={`Remove ${viewer.email}`}
                          onClick={() => {
                            setActionError(null);
                            setPendingRemoval(viewer.email);
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      {/* Same allowlist: hidden until the viewer list loads, so a non-viewer sees one message. */}
      {query.data ? <CostPricesSection /> : null}
    </div>
  );
}
